import React from 'react';
import { X, FileText, Pill, TestTube, Activity, Calendar, User, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatDateTimeIST } from '@/utils/date';
import PrescriptionMedsTable from '@/components/PrescriptionMedsTable';

const NARRATIVE_SECTIONS = [
  { key: 'chief_complaints', label: 'Chief Complaints' },
  { key: 'past_history', label: 'Past History' },
  { key: 'tumor_board', label: 'Tumor Board' },
  { key: 'clinical_examination', label: 'Clinical Examination' },
  { key: 'provisional_diagnosis', label: 'Provisional Diagnosis' },
  { key: 'final_diagnosis', label: 'Final Diagnosis' },
  { key: 'treatment_plan', label: 'Treatment Plan' },
  { key: 'visit_notes', label: 'Visit Notes' },
];

function isMeaningful(html) {
  if (!html || typeof html !== 'string') return false;
  return html.replace(/<[^>]*>/g, '').trim().length > 0;
}

function Section({ title, icon, badge, children }) {
  return (
    <div>
      <div className="flex items-center justify-between border-b border-gray-200 pb-1.5 mb-3">
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
          {icon}{title}
        </h3>
        {badge ? <Badge className="bg-gray-100 text-gray-700 text-[10px] border-0">{badge}</Badge> : null}
      </div>
      {children}
    </div>
  );
}

/** Shared past-visit drawer body (doctor + patient). */
export default function PastVisitDrawerShell({
  title, onClose, data, loading, error,
  testId = 'past-visit-drawer',
  headerClass = 'bg-gradient-to-r from-[#FFF7ED] to-white',
}) {
  const sections = data?.sections || {};
  const vitalsRaw = sections.general_info?.content || sections.general_info || {};
  const vitalsData = typeof vitalsRaw === 'object' && vitalsRaw ? vitalsRaw : {};
  const sv = sections.vitals || {};
  const vitals = [
    ['BP', vitalsData.bp || (sv.bp_systolic && sv.bp_diastolic ? `${sv.bp_systolic}/${sv.bp_diastolic}` : null)],
    ['Pulse', (vitalsData.pulse || sv.pulse) && `${vitalsData.pulse || sv.pulse} bpm`],
    ['Temp', (vitalsData.temperature || sv.temperature) && `${vitalsData.temperature || sv.temperature}°`],
    ['SpO₂', (vitalsData.spo2 || sv.spo2) && `${vitalsData.spo2 || sv.spo2}%`],
    ['Weight', (vitalsData.weight || sv.weight) && `${vitalsData.weight || sv.weight} kg`],
    ['BMI', vitalsData.bmi || sv.bmi],
  ].filter(([, v]) => v);

  const meds = sections.prescription?.items || sections.prescriptions?.items || [];
  const invs = (sections.investigations?.items || [])
    .map((t) => (typeof t === 'string' ? t : ((t && (t.name || t.test_name)) || '')))
    .filter(Boolean);
  const reviewDate = (sections.review_date && (sections.review_date.date || sections.review_date.content)) || '';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid={testId}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full md:w-[60%] bg-white shadow-2xl flex flex-col max-h-screen">
        <div className={`px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0 ${headerClass}`}>
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-5 w-5 text-[#F15A22] flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base font-bold text-[#071B34] truncate font-heading">{title || 'Past Visit Details'}</h2>
              {data ? (
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDateTimeIST(data.created_at)}</span>
                  <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />Dr. {data.doctor_name}</span>
                </p>
              ) : null}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="past-visit-close">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading visit details...
            </div>
          ) : null}
          {error && !loading ? <div className="text-center py-16 text-red-500 text-sm">{error}</div> : null}
          {data && !loading ? (
            <div className="space-y-6">
              {vitals.length > 0 ? (
                <Section icon={<Activity className="h-3.5 w-3.5 text-emerald-600" />} title="Vitals">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {vitals.map(([label, value]) => (
                      <div key={label} className="bg-emerald-50/60 border border-emerald-100 rounded-lg px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">{label}</div>
                        <div className="text-sm font-semibold text-gray-900 mt-0.5">{value}</div>
                      </div>
                    ))}
                  </div>
                </Section>
              ) : null}
              {NARRATIVE_SECTIONS.map(({ key, label }) => {
                const sec = sections[key];
                const html = (sec && typeof sec === 'object') ? (sec.content || sec.value) : sec;
                if (!isMeaningful(html)) return null;
                return (
                  <Section key={key} title={label}>
                    <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
                  </Section>
                );
              })}
              {invs.length > 0 ? (
                <Section icon={<TestTube className="h-3.5 w-3.5 text-blue-600" />} title="Investigations Ordered" badge={`${invs.length} tests`}>
                  <div className="flex flex-wrap gap-2">
                    {invs.map((name, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs font-semibold text-blue-900">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />{name}
                      </span>
                    ))}
                  </div>
                </Section>
              ) : null}
              {meds.length > 0 ? (
                <Section icon={<Pill className="h-3.5 w-3.5 text-amber-600" />} title="Prescription" badge={`${meds.length} meds`}>
                  <PrescriptionMedsTable meds={meds} />
                </Section>
              ) : null}
              {reviewDate ? (
                <Section icon={<Calendar className="h-3.5 w-3.5 text-violet-600" />} title="Next Review Date">
                  <div className="inline-flex px-3 py-1.5 bg-violet-50 border border-violet-200 rounded-md text-sm font-semibold text-violet-900">
                    {formatDateTimeIST(reviewDate) || reviewDate}
                  </div>
                </Section>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
